import Filter from '@/src/components/organism/Filter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { filters } from './filter';
import { useQuery } from '@tanstack/react-query';
import useQueryValues from '@/src/hooks/useQueryValues';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { AgGridReact } from 'ag-grid-react';
import Table from '@/src/components/atom/Table';
import { listColumn } from './column';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import { Link, useNavigate } from 'react-router-dom';
import PATH from '@/src/routes/path';
import OrderModal from './components/OrderModal';
import {
  getHomeContentInAppLinkCatetoryAPI,
  getHomeContentsAPI,
} from '@/src/apis/rest/homeContent';
import { HomeContentDto } from '@/src/models/HomeContentDto';

type ModalState = 'NONE' | 'ORDER';

const getSubmitValues = (values: Partial<HomeContentDto>) => {
  if (!values?.contentType) return values;

  const submitValues = { ...values };

  if (values.contentType !== 'EXTERNAL_LINK') {
    submitValues.content = values.contentType;
    submitValues.contentType = 'IN_APP_PAGE';
  }

  return submitValues;
};

export default function AppHomeContentPage() {
  const navigate = useNavigate();

  const { defaultValues } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(getSubmitValues(defaultValues));
  const [modalState, setModalState] = useState<ModalState>('NONE');

  // query
  const { data: homeContentInAppLinkCategoryOptions } = useQuery({
    queryKey: ['home-content-in-app-link-category-options'],
    queryFn: getHomeContentInAppLinkCatetoryAPI,
    select: data =>
      (data?.rows || []).map(item => ({ label: item.key, value: item.value })),
  });

  const contentOptions = useMemo(
    () => [
      { label: '전체', value: '' },
      ...(homeContentInAppLinkCategoryOptions || []),
      { label: '외부 링크', value: 'EXTERNAL_LINK' },
    ],
    [homeContentInAppLinkCategoryOptions],
  );

  const { data: orderHomeContentList, refetch: orderHomeContentListRefetch } =
    useQuery({
      queryKey: ['home-contents-order'],
      queryFn: () => {
        return getHomeContentsAPI({
          orderByIndex: true,
          isExposed: true,
          pageSize: 100,
        });
      },
    });

  const {
    data: listOrigin,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['home-contents', values, page, pageSize],
    queryFn: () => {
      return getHomeContentsAPI({
        ...values,
        page,
        pageSize,
      });
    },
  });

  // event
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  // effect
  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle="홈 퀵메뉴 관리"
        breadcrumbList={['홈 퀵메뉴 관리']}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={filters(contentOptions || [])}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(getSubmitValues(values));
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">홈 퀵메뉴 목록</Typo>
              <div className="flex gap-2">
                <Button
                  buttonSize="medium"
                  buttonColor="outline"
                  onClick={() => {
                    handleOpenModal('ORDER');
                  }}
                >
                  <div className="flex items-center gap-x-2 text-primary">
                    <LucideIcon name="List" color="primary" />
                    노출 순번 관리
                  </div>
                </Button>
                <Link to={PATH.APP_HOME_CONTENT_CREATE}>
                  <Button buttonSize="medium">
                    <div className="flex items-center gap-x-2">
                      <LucideIcon name="Plus" color="white" />
                      컨텐츠 등록
                    </div>
                  </Button>
                </Link>
              </div>
            </div>

            <Table
              key={contentOptions.toString()}
              ref={gridRef}
              rowData={isError ? [] : listOrigin?.rows || []}
              isPaginationDataMapping
              columnDefs={listColumn(
                e => {
                  navigate(
                    PATH.APP_HOME_CONTENT_DETAIL.replace(':id', String(e)),
                  );
                },
                key => {
                  return (
                    contentOptions.find(item => item.value === key)?.label ||
                    '외부 링크'
                  );
                },
              )}
              total={listOrigin?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>

      {/* 모달 */}
      {modalState === 'ORDER' && (
        <OrderModal
          open={modalState === 'ORDER'}
          onOpenChange={() => handleToggleModal('ORDER')}
          items={orderHomeContentList?.rows || []}
          onOkClick={() => {
            orderHomeContentListRefetch();
          }}
        />
      )}
    </>
  );
}
